import React from 'react';
import { XMarkIcon, DocumentIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import type { PendingAttachment } from './pendingAttachment';

interface AttachmentChipProps {
  attachment: PendingAttachment;
  onRemove: (localId: string) => void;
  onRetry?: (localId: string) => void;
  apiUrl: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentChip: React.FC<AttachmentChipProps> = ({ attachment, onRemove, onRetry, apiUrl }) => {
  const { localId, file, status, kind, remoteId, errorMessage, previewUrl } = attachment;
  const isImage = kind === 'image';

  // Once uploaded, prefer the remote URL so the same content the server has
  // is what the user sees — keeps preview and persisted view in sync.
  const remoteSrc = remoteId ? `${apiUrl}/chat-attachments/${remoteId}/content` : undefined;
  const imgSrc = remoteSrc ?? previewUrl;

  const errorBorder = status === 'error' ? 'border-red-500' : 'border-(--color-border-default)';

  return (
    <div
      role="listitem"
      className={`flex items-center gap-2 border ${errorBorder} bg-(--color-surface-subtle) rounded-lg pl-1 pr-2 py-1 max-w-[260px]`}
      title={file.name}
    >
      <div className="relative h-10 w-10 shrink-0 flex items-center justify-center bg-(--color-surface-page) rounded">
        {isImage && imgSrc ? (
          <img src={imgSrc} alt={file.name} className="h-10 w-10 object-cover rounded" loading="lazy" />
        ) : (
          <DocumentIcon className="h-6 w-6 text-(--color-text-muted)" />
        )}
        {status === 'uploading' && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/40 rounded">
            <LoadingSpinner className="h-5 w-5 text-white" />
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs truncate text-(--color-text-primary)">{file.name}</div>
        <div className="text-[11px] text-(--color-text-muted)">
          {status === 'error' ? (errorMessage ?? 'Upload failed') : formatBytes(file.size)}
        </div>
      </div>
      {status === 'error' && onRetry && (
        <button
          type="button"
          onClick={() => onRetry(localId)}
          aria-label={`Retry upload of ${file.name}`}
          className="p-1 text-(--color-text-muted) hover:text-(--color-text-secondary)"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        onClick={() => onRemove(localId)}
        aria-label={`Remove ${file.name}`}
        className="p-1 text-(--color-text-muted) hover:text-(--color-text-secondary)"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default AttachmentChip;
